import GuiContext from "config/context/guiContext";
import { useContext, useState, useCallback } from "react";

const INITIAL_MESSAGE =
    "Hello! I am your Inery Database Architect. What kind of application are you building today? I can help you design products, warehouse systems, user databases, or anything else with deep nested logic.";

/**
 * Sends user message to AI composition endpoint and returns the next bot message and optional structure.
 * @returns {{ sendMessage, loading, error, resetError }}
 */
export const useAIAddCompositionService = () => {
    const { URL } = useContext(GuiContext);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const sendMessage = useCallback(
        async (userMessage, history = [], sessionId = null) => {
            setLoading(true);
            setError(null);
            try {
                const res = await fetch(`${URL}/api/ai/database-architect`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({
                        message: userMessage,
                        history: history,
                        session_id: sessionId
                    })
                });
                const data = await res.json();
                if (!res.ok) {
                    setError(data?.message || data?.error || "Request failed");
                    return { message: null, structure: null, sessionId: sessionId || null };
                }
                if (!data.status) {
                    setError(data?.message || data?.error || "AI request failed");
                    return { message: null, structure: null, sessionId: sessionId || null };
                }
                return {
                    message: data.message ?? "",
                    structure: data.structure ?? null,
                    sessionId: data.session_id ?? data.sessionId ?? sessionId ?? null
                };
            } catch (err) {
                setError(err.message || "An error occurred");
                return { message: null, structure: null, sessionId: sessionId || null };
            } finally {
                setLoading(false);
            }
        },
        [URL]
    );

    const resetError = useCallback(() => setError(null), []);

    return {
        sendMessage,
        loading,
        error,
        resetError,
        initialMessage: INITIAL_MESSAGE
    };
};
